import { useEffect, useState } from "react";
import { Button } from "@mui/material";
import { tss } from "tss-react";
import ViewerEvents from "events/ViewerEvents";
import * as Utils from "services/UtilsService";
import commonStyles, { panelWidth } from "styles/common";

const useStyles = tss.create({
    root: {
        width: panelWidth - 40,
        minHeight: 120,
    },
    icon: {
        position: "relative",
        width: 16,
        height: 16,
        verticalAlign: "middle",
        display: "inline-block",
    },
    title: {
        display: "inline-block",
        position: "relative",
        fontWeight: 600,
        left: 5,
    },
    button: {
        width: 30,
        height: 30,
        minWidth: 30,
    },
    buttonDiv: {
        width: panelWidth - 40,
        textAlign: "center",
        position: "relative",
    },
    content: {
        marginTop: 20,
        height: 170,
        width: panelWidth - 40,
    },
    table: {
        width: panelWidth - 40,
        border: 0,
    },
    bullet: {
        "&::before": {
            content: '"\\2022"',
            color: "#E6E6E6",
            fontWeight: "bold",
            display: "inline-block",
            width: 27,
            fontSize: "19.5px",
        },
        "&::after": {
            content: '"\\2022"',
            color: "#E6E6E6",
            fontWeight: "bold",
            display: "inline-block",
            width: 27,
            fontSize: "19.5px",
            marginLeft: -25,
        },
    },
    rod: {
        display: "inline-block",
        width: "80%",
        borderTop: "1px solid #E6E6E6",
        position: "relative",
        top: 1,
        left: -12,
    },
    topCell: {
        width: "55%",
        paddingLeft: 7,
        height: 30,
    },
    cell: {
        width: "55%",
        paddingLeft: 15,
        height: 30,
    },
});

let panel: any, button: any, content: any;

type IProps = {
    show: boolean;
};

export default function MeasurePanel(props: IProps) {
    const { classes, cx } = useStyles();
    const { classes: common } = commonStyles();
    const { show } = props;
    const viewer = ViewerEvents.viewer;

    const [data, setData] = useState({
        length: 0,
        bearing: 0,
        x1: 0,
        y1: 0,
        z1: 0,
        x2: 0,
        y2: 0,
        z2: 0,
    });

    useEffect(() => {
        const stopMeasurement = () => {
            if (viewer) {
                viewer.deleteSegment("MeasureSegmentId");
            }
        };
        return () => {
            stopMeasurement();
        };
    }, [show, viewer]);

    const handleKeyDown = (evt: any) => {
        if (evt.keyCode === 27) {
            if (button) {
                button.innerHTML = "Make Measurement";
            }
            viewer.deleteSegment("MeasureSegmentId");
            document.removeEventListener("keydown", handleKeyDown, false);
        }
    };
    const getBearing = (dx: number, dy: number) => {
        let d = Math.sqrt(dx * dx + dy * dy);
        if (d <= Number.EPSILON) return 0.0;

        var degrees = Math.asin(dy / d) / 0.01745329251;
        return Utils.round(degrees);
    };
    const makeMeasurement = () => {
        if (button && button !== null && viewer) {
            if (button.innerHTML.indexOf("Make") !== -1) {
                button.innerHTML = "Cancel Measurement";
                document.addEventListener("keydown", handleKeyDown, false);
                //console.log("call startSegment in viewer");
                viewer.startSegment(
                    "MeasureSegmentId",
                    0,
                    (measure: any) => {
                        //console.log("startSegment, completeCallback:", measure);
                        if (panel && content) {
                            button.innerHTML = "Make Measurement";
                            panel.style.height = 240;
                            content.style.display = "block";
                            const pointA: any = measure.wPointA;
                            const pointB: any = measure.wPointB;
                            const dx: number = measure.wPointB[0] - measure.wPointA[0];
                            const dy: number = measure.wPointB[1] - measure.wPointA[1];
                            const dz: number = measure.wPointB[2] - measure.wPointA[2];
                            const length = Utils.round(Math.sqrt(dx * dx + dy * dy + dz * dz));
                            const bearing = getBearing(Math.abs(dx), Math.abs(dy));
                            setData({
                                length: length,
                                bearing: bearing,
                                x1: Utils.round(pointA[0]),
                                x2: Utils.round(pointB[0]),
                                y1: Utils.round(pointA[1]),
                                y2: Utils.round(pointB[1]),
                                z1: Utils.round(pointA[2]),
                                z2: Utils.round(pointB[2]),
                            });
                        }
                    },
                    (obj: any) => {
                        //console.log("startSegment, cancelCallback", obj);
                        if (button && button !== null) {
                            button.innerHTML = "Cancel Measurement";
                            viewer.deleteSegment("MeasureSegmentId");
                        }
                    },
                    (obj: any) => {
                        //console.log("startSegment, deleteCallback", obj);
                    }
                );
            } else {
                button.innerHTML = "Make Measurement";
                document.removeEventListener("keydown", handleKeyDown, false);
                viewer.deleteSegment("MeasureSegmentId");
            }
        }
    };

    return (
        <div className={cx(classes.root, common.middleCenter)}>
            {show ? (
                <div ref={(elem) => (panel = elem)} className={common.middleCenter}>
                    <div className={classes.buttonDiv}>
                        <Button
                            ref={(elm) => (button = elm)}
                            onClick={(e) => {
                                makeMeasurement();
                            }}
                            aria-label="Make Measurement"
                            title="Make Measurement"
                            variant="contained"
                            color="primary"
                        >
                            Make Measurement
                        </Button>

                        <div ref={(elm) => (content = elm)} style={{ display: "none" }} className={classes.content}>
                            <table className={classes.table}>
                                <tbody>
                                    <tr>
                                        <td align="left" className={classes.topCell}>
                                            Length: {data.length}{" "}
                                        </td>
                                        <td align="left" className={classes.topCell}>
                                            Bearing: {data.bearing}
                                            <span>&#176;</span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan={2}>
                                            <div className={classes.bullet}>
                                                <hr className={classes.rod} />
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td align="left" className={classes.cell}>
                                            x {data.x1}
                                        </td>
                                        <td align="left" className={classes.cell}>
                                            x {data.x2}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td align="left" className={classes.cell}>
                                            y {data.y1}
                                        </td>
                                        <td align="left" className={classes.cell}>
                                            y {data.y2}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td align="left" className={classes.cell}>
                                            z {data.z1}
                                        </td>
                                        <td align="left" className={classes.cell}>
                                            z {data.z2}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            ) : (
                ""
            )}
        </div>
    );
}
